import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:5000' || 'http://localhost:5000';

export interface User {
  id: string
  email: string
  username: string
  userType: 'admin' | 'agent' | 'engineer' | 'client'
  first_name?: string
  last_name?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  clientType?: string
  industry?: string
  status: string
  profileImage?: string
  emailVerified: boolean
  notes?: string
  last_login?: string
  created_at?: string
  updated_at?: string
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null)
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // Computed properties
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userType = computed(() => user.value?.userType || null)
  const fullName = computed(() => {
    if (!user.value) return ''
    const firstName = user.value.first_name || ''
    const lastName = user.value.last_name || ''
    return `${firstName} ${lastName}`.trim()
  })

  // Initialize auth from storage
  const initializeFromStorage = () => {
    if (isInitialized.value) return

    try {
      console.log('🔄 Initializing auth from storage...')

      // Try combined auth format first (for roleHandler compatibility)
      const combinedAuth = localStorage.getItem('auth') || sessionStorage.getItem('auth')
      if (combinedAuth) {
        try {
          const parsed = JSON.parse(combinedAuth)
          if (parsed.token && parsed.user) {
            token.value = parsed.token
            user.value = parsed.user
            console.log('✅ Auth initialized from combined storage')
            isInitialized.value = true
            return
          }
        } catch (e) {
          console.warn('Failed to parse combined auth format:', e)
        }
      }

      // Fall back to separate token/user format
      const storedToken = localStorage.getItem('auth_token')
      const storedUser = localStorage.getItem('user_data')

      const tokenToUse = storedToken || sessionStorage.getItem('auth_token')
      const userToUse = storedUser || sessionStorage.getItem('user_data')

      if (tokenToUse && userToUse) {
        token.value = tokenToUse
        user.value = JSON.parse(userToUse)
        console.log('✅ Auth initialized from separate storage')
      } else {
        console.log('⚠️ No auth data found in storage')
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('Failed to initialize auth from storage:', errorMessage)
      clearAuth()
    } finally {
      isInitialized.value = true
    }
  }

  // Clear all auth data
  const clearAuth = () => {
    token.value = null
    user.value = null
    error.value = null

    // Clear all storage formats
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('auth')
    localStorage.removeItem('user_type')
    sessionStorage.removeItem('auth_token')
    sessionStorage.removeItem('user_data')
    sessionStorage.removeItem('auth')
    sessionStorage.removeItem('user_type')
  }

  // Set token only
  const setToken = (newToken: string) => {
    token.value = newToken
  }

  // Set user only
  const setUser = (newUser: User) => {
    user.value = newUser
  }

  // Update user profile image
  const updateProfileImage = (imageUrl: string) => {
    if (user.value) {
      user.value.profileImage = imageUrl

      // Update in all storage formats
      const userDataStr = JSON.stringify(user.value)

      // Update user_data in both storages
      if (localStorage.getItem('user_data')) {
        localStorage.setItem('user_data', userDataStr)
      }
      if (sessionStorage.getItem('user_data')) {
        sessionStorage.setItem('user_data', userDataStr)
      }

      // Update combined auth format
      if (localStorage.getItem('auth')) {
        const authData = JSON.parse(localStorage.getItem('auth')!)
        authData.user = user.value
        localStorage.setItem('auth', JSON.stringify(authData))
      }
      if (sessionStorage.getItem('auth')) {
        const authData = JSON.parse(sessionStorage.getItem('auth')!)
        authData.user = user.value
        sessionStorage.setItem('auth', JSON.stringify(authData))
      }
    }
  }

  // Set authentication data
  const setAuth = (newToken: string, newUser: User, rememberMe = false) => {
    token.value = newToken
    user.value = newUser

    // Create combined auth object for roleHandler compatibility
    const authData = JSON.stringify({
      token: newToken,
      user: newUser
    })

    if (rememberMe) {
      // Store in localStorage for persistent login
      localStorage.setItem('auth_token', newToken)
      localStorage.setItem('user_data', JSON.stringify(newUser))
      localStorage.setItem('auth', authData) // For roleHandler
      localStorage.setItem('user_type', newUser.userType) // For roleHandler

      // Clear session storage
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('user_data')
      sessionStorage.removeItem('auth')
      sessionStorage.removeItem('user_type')
    } else {
      // Store in sessionStorage for session-only login
      sessionStorage.setItem('auth_token', newToken)
      sessionStorage.setItem('user_data', JSON.stringify(newUser))
      sessionStorage.setItem('auth', authData) // For roleHandler
      sessionStorage.setItem('user_type', newUser.userType) // For roleHandler

      // Clear local storage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      localStorage.removeItem('auth')
      localStorage.removeItem('user_type')
    }
  }

  // Login method
  const login = async (email: string, password: string, rememberMe = false) => {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Login failed')
      }

      console.log('🔑 Login response:', data)

      if (!data.user || !data.user.userType) {
        throw new Error('Invalid user data received from server')
      }

      setAuth(data.token, data.user, rememberMe)
      return { token: data.token, user: data.user }
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      console.error('❌ Auth store login error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  // Logout method
  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST'
      })
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('Logout error:', errorMessage)
    } finally {
      clearAuth()
    }
  }

  // Check authentication status with server (optional)
  const checkAuth = async () => {
    if (!token.value) return false

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })

      if (!response.ok) {
        clearAuth()
        return false
      }

      const data = await response.json()
      user.value = data.user

      // Update storage with latest user data
      if (localStorage.getItem('auth')) {
        const authData = JSON.parse(localStorage.getItem('auth')!)
        authData.user = data.user
        localStorage.setItem('auth', JSON.stringify(authData))
      }
      if (sessionStorage.getItem('auth')) {
        const authData = JSON.parse(sessionStorage.getItem('auth')!)
        authData.user = data.user
        sessionStorage.setItem('auth', JSON.stringify(authData))
      }

      return true
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('Auth check error:', errorMessage)
      clearAuth()
      return false
    }
  }

  return {
    // State
    token,
    user,
    loading,
    error,
    isInitialized,

    // Computed
    isAuthenticated,
    userType,
    fullName,

    // Actions
    initializeFromStorage,
    setAuth,
    setToken,
    setUser,
    updateProfileImage,
    login,
    logout,
    checkAuth,
    clearAuth
  }
})
